import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";

// Placeholder-mount orchestrator for a full-React page. What remains here is:
//   - the tooltip + form-field placeholder mounts (React islands rendered into
//     server-emitted placeholders);
//   - a `.wistia_embed` scan that loads the Wistia library for the sealy advert
//     popovers (static markup with no React owner of its own).
// Everything else this used to do now lives in the owning component (accordions,
// message dismiss, wistia background videos, the sealy mobile nav via
// useMobileMenuDialog) or a PageSkeleton hook (link rewriting, promo-callout
// fills, form-error focus).
export const initWidgets = async (lazy = true): Promise<void> => {
    const document = getDocument();
    if (!document) return;

    const loading: Array<Promise<unknown>> = [];

    // Tooltips
    const tooltips = document.querySelectorAll<HTMLElement>("[data-tooltip]");
    if (tooltips) {
        const { initTooltips } = await import("./tooltips");
        initTooltips(lazy);
    }

    // Forms
    const formFields = document.querySelectorAll<HTMLElement>(
        ".form-field-placeholder",
    );
    if (formFields) {
        const { initFormFields } = await import("./initFormFields");
        Array.from(formFields).forEach((formField) => {
            loading.push(initFormFields(formField));
        });
    }

    // Wistia Videos. This scan only loads the Wistia library (it never
    // per-element embeds), and loadWistiaPlayer() is idempotent — so a React
    // component that renders `.wistia_embed` and loads Wistia itself (e.g. the
    // cocoon video-bar popover) needs no opt-out: an extra call here is a no-op.
    const videos = document.querySelectorAll<HTMLElement>(".wistia_embed");
    if (videos) {
        const { loadWistiaPlayer } = await import("../utils/wistia");
        if (videos.length > 0) {
            void loadWistiaPlayer().catch(() => {});
        }
    }

    // Wait for everything to finish loading
    await Promise.all(loading);
};
